/**
 * Calm Interventions Component
 * 
 * Module 3C: Budget Realignment Suggestions
 * Displays calm, non-judgmental suggestions for budget adjustments
 * when overspend patterns are detected.
 */
import { useState } from "react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { 
  Lightbulb,
  ArrowRight,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Loader2,
  TrendingUp,
  Repeat,
  Search as SearchIcon,
} from "lucide-react";
import api from "../../lib/api";

// Format currency
function formatCurrency(amount, currency = 'NZD') {
  return new Intl.NumberFormat('en-NZ', { 
    style: 'currency', 
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  }).format(Math.abs(amount));
}

// Single Realignment Option
function RealignmentOption({ option, currency, onApply, isApplying }) {
  const getIcon = () => {
    switch (option.intervention_type) {
      case 'increase_budget':
        return <TrendingUp className="w-5 h-5 text-emerald-600" strokeWidth={2.25} />;
      case 'redistribute':
        return <Repeat className="w-5 h-5 text-teal-600" strokeWidth={2.25} />;
      case 'review_spending':
        return <SearchIcon className="w-5 h-5 text-indigo-600" strokeWidth={2.25} />;
      default:
        return <Lightbulb className="w-5 h-5 text-amber-600" strokeWidth={2.25} />;
    }
  };

  const isReviewOnly = option.intervention_type === 'review_spending';

  return (
    <div className="flex items-start justify-between gap-4 p-3 bg-white rounded-lg border border-[#1a4d3e]/10">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex items-center justify-center">{getIcon()}</span>
        <div>
          <p className="text-sm">{option.rationale}</p>
          {!isReviewOnly && (
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <span>{formatCurrency(option.current_weekly, currency)}/wk</span>
              <ArrowRight className="w-3 h-3" />
              <span className="font-medium text-[#1a4d3e]">
                {formatCurrency(option.suggested_weekly, currency)}/wk
              </span>
              {option.source_category_name && (
                <span className="text-[#1a4d3e]/60">
                  (from {option.source_category_name})
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant={isReviewOnly ? "outline" : "default"}
        onClick={() => onApply(option.option_id)}
        disabled={isApplying}
        className={isReviewOnly ? "" : "bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"}
        data-testid={`apply-option-${option.option_id}`}
      >
        {isApplying ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isReviewOnly ? (
          'Got it'
        ) : (
          'Apply'
        )}
      </Button>
    </div>
  );
}

// Single Intervention Card
function InterventionCard({ intervention, currency, onApply, applyingId }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div 
      className="bg-[#1a4d3e]/5 rounded-xl overflow-hidden"
      data-testid={`intervention-${intervention.category_id}`}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start justify-between gap-3 p-4 text-left hover:bg-[#1a4d3e]/10 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Lightbulb className="w-5 h-5 text-[#1a4d3e]" />
          </div>
          <div>
            <h4 className="font-medium text-[#1a4d3e]">
              {intervention.category_name}
            </h4>
            <p className="text-sm text-[#1a4d3e]/70 mt-0.5">
              {intervention.message}
            </p>
            <div className="flex items-center gap-3 mt-2 text-xs text-[#1a4d3e]/50">
              <span>{intervention.consecutive_weeks} weeks over plan</span>
              <span>•</span>
              <span>~{formatCurrency(intervention.avg_weekly_overspend, currency)}/week average</span>
            </div>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-[#1a4d3e]/50 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#1a4d3e]/50 flex-shrink-0" />
        )}
      </button>

      {/* Options */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          <p className="text-xs font-medium text-[#1a4d3e]/60 mb-2 pl-1">
            Choose an option:
          </p>
          {intervention.realignment_options.map((option) => (
            <RealignmentOption
              key={option.option_id}
              option={option}
              currency={currency}
              onApply={onApply}
              isApplying={applyingId === option.option_id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Main Calm Interventions Component
export default function CalmInterventions({ interventions, currency = 'NZD', onUpdate, debugMode = false }) {
  const [applyingId, setApplyingId] = useState(null);
  const [dismissedIds, setDismissedIds] = useState([]);

  if (!interventions || interventions.length === 0) {
    return null;
  }

  // Filter out dismissed interventions
  const activeInterventions = interventions.filter(
    i => !dismissedIds.includes(i.category_id)
  );

  if (activeInterventions.length === 0) {
    return null;
  }

  const handleApply = async (optionId) => {
    setApplyingId(optionId);
    
    // Debug mode: simulate success without hitting API
    if (debugMode) {
      console.log('[DEBUG] Mock applying intervention:', optionId);
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
      toast.success('Budget updated (debug mode)');
      
      // Find which intervention this belongs to and dismiss it
      const intervention = interventions.find(i => 
        i.realignment_options.some(o => o.option_id === optionId)
      );
      if (intervention) {
        setDismissedIds(prev => [...prev, intervention.category_id]);
      }
      setApplyingId(null);
      return;
    }
    
    try {
      const resp = await api.interventions.apply(optionId);
      
      if (resp.ok) {
        const data = await resp.json();
        toast.success(data.message || 'Budget updated');
        
        // Find which intervention this belongs to and dismiss it
        const intervention = interventions.find(i => 
          i.realignment_options.some(o => o.option_id === optionId)
        );
        if (intervention) {
          setDismissedIds(prev => [...prev, intervention.category_id]);
        }
        
        // Notify parent to refresh data
        onUpdate?.();
      } else {
        const err = await resp.json();
        toast.error(err.detail || 'Failed to apply');
      }
    } catch (error) {
      console.error('Apply error:', error);
      toast.error('Something went wrong');
    } finally {
      setApplyingId(null);
    }
  };

  const handleDismiss = (categoryId) => {
    setDismissedIds(prev => [...prev, categoryId]);
    toast.info('Suggestion dismissed');
  };

  return (
    <div className="space-y-4" data-testid="calm-interventions">
      {/* Debug Mode Banner */}
      {debugMode && (
        <div className="px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg text-xs text-amber-800 text-center">
          Debug Mode: Mock overspend data for visual testing
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-[#1a4d3e]">
            Budget Realignment
          </h3>
          <p className="text-sm text-[#1a4d3e]/60">
            Some categories might benefit from a small adjustment
          </p>
        </div>
      </div>

      {/* Intervention Cards */}
      <div className="space-y-3">
        {activeInterventions.map((intervention) => (
          <InterventionCard
            key={intervention.category_id}
            intervention={intervention}
            currency={currency}
            onApply={handleApply}
            applyingId={applyingId}
          />
        ))}
      </div>

      {/* Calm footer message */}
      <p className="text-xs text-center text-[#1a4d3e]/40 pt-2">
        These are suggestions, not requirements. Your budget, your choice.
      </p>
    </div>
  );
}
